<template>
  <div>
    <div class="mt-2">{{ displayValue }}</div>
    <div v-if="debug" class="mt-3">
      <div class="form-check mb-2">
        <input class="form-check-input" type="checkbox" v-model="debugExpanded" id="co2DebugToggle">
        <label class="form-check-label" for="co2DebugToggle">Toon debug details</label>
      </div>
      <div v-if="debugExpanded" class="table-responsive">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Spec. bouwdeel</th>
              <th>OppKey</th>
              <th>Typologie</th>
              <th>Opp.m²/ww</th>
              <th>#woningen</th>
              <th>Opp_T</th>
              <th>Materiaal</th>
              <th>CO2 ref</th>
              <th>CO2 mat</th>
              <th>Bron</th>
              <th>ΔCO2</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in debugRows" :key="r.key">
              <td>{{ r.key }}</td>
              <td>{{ r.oppKey }}</td>
              <td>{{ r.typology }}</td>
              <td>{{ format(r.areaPerDwelling) }}</td>
              <td>{{ format(r.num) }}</td>
              <td>{{ format(r.oppT) }}</td>
              <td>{{ r.material ?? '-' }}</td>
              <td>{{ format(r.ref) }}</td>
              <td>{{ format(r.mat) }}</td>
              <td>{{ r.matSource ?? '-' }}</td>
              <td>{{ format(r.delta) }}</td>
              <td><span :class="r.ok ? 'text-success' : 'text-danger'">{{ r.status }}</span></td>
            </tr>
          </tbody>
        </table>
        <div class="small" :class="overallOk ? 'text-success' : 'text-danger'">{{ overallStatus }}</div>
      </div>
    </div>
  </div>
  </template>

<script>
export default {
  name: 'FormElementCO2',
  props: ['element','formData','lang','hidden','targetId','wasValidated'],
  emits: ['elementChanged'],
  data() {
    return { value: null, debugExpanded: false, debugRows: [], overallStatus: '', overallOk: true }
  },
  computed: {
    debug() {
      return !!(this.element?.co2Config?.debug)
    },
    displayValue() {
      if (this.value === null || this.value === undefined) return '-'
      const suffix = this.element?.co2Config?.suffix || ''
      return `${this.value.toFixed(2)}${suffix ? ' ' + suffix : ''}`
    }
  },
  watch: {
    formData: { deep: true, handler() { this.recompute() } },
    element: { deep: true, handler() { this.recompute() } }
  },
  methods: {
    format(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '-'
      try {
        return n.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      } catch (e) {
        return n.toFixed(2)
      }
    },
    getFieldValue(name) {
      if (!name) return undefined
      return this.formData?.[name]
    },
    getSelectValue(name) {
      const v = this.getFieldValue(name)
      if (!v) return undefined
      if (typeof v === 'string') return v
      if (v && v.value) return v.value
      return v
    },
    recompute() {
      try {
        const cfg = this.element?.co2Config
        if (!cfg) return

        const typology = this.getSelectValue(cfg.typologyField)
        const num = Number(this.getFieldValue(cfg.housesField))
        const t1Rows = cfg.table1?.rows || []
        const t2Mats = cfg.table2?.materials || []
        const t2Rows = cfg.table2?.rows || []

        let total = 0
        const dbg = []
        let allOk = true
        
        for (const r of t2Rows) {
          const line = { key: r.key, oppKey: r.oppKey, typology, status: '', ok: true }
          // lookup materiaal gekozen in formulier
          const material = this.getSelectValue(r.selectPropName)
          line.material = material
          if (!material) { line.ok = false; line.status = 'Geen materiaalkeuze'; dbg.push(line); allOk = false; continue }
          if (!t2Mats.includes(material)) { line.ok = false; line.status = `Materiaal '${material}' niet in tabel2`; dbg.push(line); allOk = false; continue }

          // area per dwelling uit tabel1
          const t1Row = t1Rows.find(rr => rr.typology === typology)
          const areaPerDwelling = t1Row?.values?.[r.oppKey]
          line.areaPerDwelling = areaPerDwelling
          if (!(Number(areaPerDwelling) > 0)) { line.ok = false; line.status = `Geen opp voor '${r.oppKey}' & typologie`; dbg.push(line); allOk = false; continue }

          if (!(Number(num) > 0)) { line.ok = false; line.status = 'Aantal woningen ontbreekt'; dbg.push(line); allOk = false; continue }
          line.num = num
          const oppT = Number(areaPerDwelling) * Number(num)
          line.oppT = oppT

          // Referentie: vereist en numeriek
          const ref = (r.reference === null || r.reference === undefined || r.reference === '') ? null : Number(r.reference)
          line.ref = ref
          if (!Number.isFinite(ref)) { line.ok = false; line.status = 'Referentiewaarde ontbreekt/ongeldig'; dbg.push(line); allOk = false; continue }

          // Materiaalwaarde: gebruik materiaal als aanwezig, anders forfaitair, anders geen berekening
          const rawMat = r.values?.[material]
          const matNum = (rawMat === null || rawMat === undefined || rawMat === '') ? null : Number(rawMat)
          let usedMat = Number.isFinite(matNum) ? matNum : null
          let source = 'materiaal'
          if (!Number.isFinite(usedMat)) {
            const f = (r.forfaitair === null || r.forfaitair === undefined || r.forfaitair === '') ? null : Number(r.forfaitair)
            if (Number.isFinite(f)) { usedMat = f; source = 'forfaitair' }
          }
          if (!Number.isFinite(usedMat)) { line.ok = false; line.status = 'Geen materiaalwaarde en forfaitair ontbreekt'; dbg.push(line); allOk = false; continue }

          line.mat = usedMat
          line.matSource = source

          const delta = ref - usedMat
          line.delta = delta
          const saving = oppT * delta
          total += saving
          line.status = 'ok'
          dbg.push(line)
        }

        this.value = Number.isFinite(total) ? total : null
        this.debugRows = dbg
        this.overallOk = allOk
        this.overallStatus = allOk ? 'Berekening succesvol' : 'Berekening onvolledig – zie regels'
        // emit enkel de computed waarde
        this.$emit('elementChanged', this.element.name, this.value)
      } catch (e) {
        this.value = null
        this.overallOk = false
        this.overallStatus = `Fout tijdens berekening: ${e?.message || e}`
      }
    }
  },
  mounted() {
    this.recompute()
  }
}
</script>

<style scoped>
.text-danger { color: #dc3545 !important; }
.text-success { color: #198754 !important; }
</style>


